import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface ChatUnreadStore {
  unreadProposalIds: string[];
  activeProposalId: string | null; // The ID of the conversation currently being viewed
  markAsUnread: (proposalId: string) => void;
  markAsRead: (proposalId: string) => void;
  isUnread: (proposalId: string) => boolean;
  setActiveProposalId: (proposalId: string | null) => void;
  clearAll: () => void;
}

/**
 * Zustand store for persisting unread conversation state across page refreshes
 * Uses localStorage to persist unread proposalIds
 */
export const useChatUnreadStore = create<ChatUnreadStore>()(
  persist(
    (set, get) => ({
      unreadProposalIds: [],
      activeProposalId: null,

      markAsUnread: (proposalId: string) =>
        set((state) => {
          if (state.unreadProposalIds.includes(proposalId)) return state;
          return { unreadProposalIds: [...state.unreadProposalIds, proposalId] };
        }),

      markAsRead: (proposalId: string) =>
        set((state) => ({
          unreadProposalIds: state.unreadProposalIds.filter((id) => id !== proposalId),
        })),

      isUnread: (proposalId: string) => {
        return get().unreadProposalIds.includes(proposalId);
      },

      setActiveProposalId: (proposalId: string | null) => set({ activeProposalId: proposalId }),

      clearAll: () => set({ unreadProposalIds: [] }),
    }),
    {
      name: "chat-unread-storage",
      storage: createJSONStorage(() => localStorage),
      // Only persist the unread list, not the current active view (which is session-specific)
      partialize: (state) => ({ unreadProposalIds: state.unreadProposalIds }),
    }
  )
);
